import { Fragment, useState } from 'react';
import Panel from './Panel';
import SummaryCard from './SummaryCard';
import { exportElSalvadorCsv, exportElSalvadorExcel } from './exportElSalvador';

const moneyFmt = new Intl.NumberFormat('es-SV', { style: 'currency', currency: 'USD' });
const numberFmt = new Intl.NumberFormat('es-SV', { minimumFractionDigits: 2, maximumFractionDigits: 2 });

const STATUS_LABELS = {
  completed: 'Completado',
  voided: 'Anulado',
  draft: 'Borrador',
  unknown: 'Desconocido',
};

const STATUS_STYLES = {
  completed: 'bg-emerald-50 text-emerald-700',
  voided: 'bg-red-50 text-red-700',
  draft: 'bg-amber-50 text-amber-700',
  unknown: 'bg-gray-100 text-gray-500',
};

function LineItemsRow({ invoice }) {
  return (
    <tr>
      <td colSpan={8} className="px-6 py-4 bg-indigo-50/40 border-b border-indigo-100">
        <p className="text-xs text-indigo-400 font-mono mb-3 break-all">{invoice.generationCode}</p>
        {invoice.items?.length ? (
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr className="text-left text-gray-500 border-b border-indigo-100">
                  <th className="pb-1.5 pr-4 font-medium">N°</th>
                  <th className="pb-1.5 pr-4 font-medium">Cantidad</th>
                  <th className="pb-1.5 pr-4 font-medium min-w-[200px]">Descripción</th>
                  <th className="pb-1.5 pr-4 font-medium text-right">P. Unitario</th>
                  <th className="pb-1.5 font-medium text-right">Venta Gravada</th>
                </tr>
              </thead>
              <tbody>
                {invoice.items.map((item, j) => (
                  <tr key={j} className="border-t border-indigo-100/60">
                    <td className="py-1.5 pr-4 text-gray-400">{item.numItem}</td>
                    <td className="py-1.5 pr-4 text-gray-700">{item.quantity}</td>
                    <td className="py-1.5 pr-4 text-gray-800">{item.description}</td>
                    <td className="py-1.5 pr-4 text-right tabular-nums text-gray-600">{numberFmt.format(item.unitPrice)}</td>
                    <td className="py-1.5 text-right tabular-nums font-semibold text-gray-800">{numberFmt.format(item.taxedAmount)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        ) : (
          <p className="text-xs text-gray-400">Sin líneas de detalle.</p>
        )}
        {invoice.warnings?.length > 0 && (
          <ul className="mt-3 space-y-0.5">
            {invoice.warnings.map((w, i) => (
              <li key={i} className="text-amber-600 text-xs">⚠ {w}</li>
            ))}
          </ul>
        )}
      </td>
    </tr>
  );
}

export default function ElSalvadorPanel({ invoices, errors, onDismissErrors }) {
  const [expandedRows, setExpandedRows] = useState(new Set());

  const toggleRow = (id) => {
    setExpandedRows(prev => {
      const next = new Set(prev);
      next.has(id) ? next.delete(id) : next.add(id);
      return next;
    });
  };

  const totalGravadas = invoices.reduce((s, inv) => s + inv.totals.gravadas, 0);
  const totalIva = invoices.reduce((s, inv) => s + inv.totals.iva, 0);
  const totalPagar = invoices.reduce((s, inv) => s + inv.totals.totalPagar, 0);

  return (
    <div id="panel-el-salvador" className="space-y-4">
      <div className="flex items-center justify-between">
        <div>
          <h2 className="text-base font-bold text-gray-900 leading-none">El Salvador</h2>
          <p className="text-xs text-gray-400 mt-1">Documentos Tributarios Electrónicos · Ministerio de Hacienda</p>
        </div>
        {invoices.length > 0 && (
          <div className="flex gap-2">
            <button onClick={() => exportElSalvadorCsv(invoices)} className="text-xs bg-gray-100 hover:bg-gray-200 text-gray-700 rounded-lg px-3 py-1.5 font-medium transition-colors">
              Exportar CSV
            </button>
            <button onClick={() => exportElSalvadorExcel(invoices)} className="text-xs bg-brand-600 hover:bg-brand-700 text-white rounded-lg px-3 py-1.5 font-medium transition-colors">
              Exportar Excel
            </button>
          </div>
        )}
      </div>

      {errors.length > 0 && (
        <div className="bg-red-50 border border-red-200 rounded-xl p-4">
          <div className="flex items-center justify-between mb-2">
            <h3 className="text-red-700 font-semibold text-sm">
              {errors.length} archivo{errors.length !== 1 ? 's' : ''} de El Salvador con error
            </h3>
            <button onClick={onDismissErrors} className="text-red-400 hover:text-red-600 text-xs">Descartar</button>
          </div>
          <ul className="space-y-1">
            {errors.map((e, i) => (
              <li key={i} className="text-red-600 text-xs">
                <span className="font-mono font-medium">{e.fileName}</span>
                {' — '}{e.error}
              </li>
            ))}
          </ul>
        </div>
      )}

      {invoices.length === 0 && errors.length === 0 && (
        <p className="text-center text-gray-400 text-sm py-8">
          Importa Documentos Tributarios Electrónicos de El Salvador (JSON, XML o ZIP) para comenzar
        </p>
      )}

      {invoices.length > 0 && (
        <>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
            <SummaryCard label="DTE cargados" value={invoices.length} />
            <SummaryCard label="Total Gravadas" value={moneyFmt.format(totalGravadas)} />
            <SummaryCard label="Total IVA" value={moneyFmt.format(totalIva)} />
            <SummaryCard label="Total a Pagar" value={moneyFmt.format(totalPagar)} isHighlight />
          </div>

          <Panel title={`${invoices.length} documento${invoices.length !== 1 ? 's' : ''} · Clic en una fila para ver el detalle`}>
            <div className="overflow-x-auto -mx-4 -mb-4">
              <table className="w-full text-sm">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100">
                    <th className="w-8 px-3 py-3" />
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 whitespace-nowrap">Fecha</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 hidden lg:table-cell">Tipo</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Emisor</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600 hidden md:table-cell">Receptor</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 hidden lg:table-cell whitespace-nowrap">Gravadas</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 hidden lg:table-cell whitespace-nowrap">IVA</th>
                    <th className="px-4 py-3 text-right font-semibold text-gray-600 whitespace-nowrap">Total</th>
                    <th className="px-4 py-3 text-left font-semibold text-gray-600">Estado</th>
                  </tr>
                </thead>
                <tbody>
                  {invoices.map((inv, i) => {
                    const isExpanded = expandedRows.has(inv.id);
                    return (
                      <Fragment key={inv.id || i}>
                        <tr
                          className={`border-b border-gray-100 cursor-pointer hover:bg-brand-500/5 transition-colors ${
                            i % 2 === 0 ? 'bg-white' : 'bg-gray-50/40'
                          } ${isExpanded ? 'bg-indigo-50/30' : ''}`}
                          onClick={() => toggleRow(inv.id)}
                        >
                          <td className="px-3 py-3 text-gray-400 text-xs text-center select-none">
                            {isExpanded ? (
                              <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} className="inline text-indigo-400">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M19.5 8.25l-7.5 7.5-7.5-7.5" />
                              </svg>
                            ) : (
                              <svg width="12" height="12" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5} className="inline text-gray-300">
                                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
                              </svg>
                            )}
                          </td>
                          <td className="px-4 py-3 text-gray-700 whitespace-nowrap font-mono text-xs">{inv.date}</td>
                          <td className="px-4 py-3 hidden lg:table-cell">
                            <span className="bg-gray-100 text-gray-500 text-xs px-2 py-0.5 rounded">{inv.tipoDteLabel}</span>
                          </td>
                          <td className="px-4 py-3">
                            <div className="font-medium text-gray-800 leading-snug">{inv.issuer.name}</div>
                            {inv.issuer.nit && <div className="text-gray-400 text-xs font-mono">{inv.issuer.nit}</div>}
                          </td>
                          <td className="px-4 py-3 text-gray-600 hidden md:table-cell text-xs leading-snug">{inv.receiver.name}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-700 hidden lg:table-cell">{numberFmt.format(inv.totals.gravadas)}</td>
                          <td className="px-4 py-3 text-right tabular-nums text-gray-500 hidden lg:table-cell">{numberFmt.format(inv.totals.iva)}</td>
                          <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-900">{numberFmt.format(inv.totals.totalPagar)}</td>
                          <td className="px-4 py-3">
                            <span className={`text-xs px-2 py-0.5 rounded font-medium ${STATUS_STYLES[inv.status] || STATUS_STYLES.unknown}`}>
                              {STATUS_LABELS[inv.status] || STATUS_LABELS.unknown}
                            </span>
                          </td>
                        </tr>
                        {isExpanded && <LineItemsRow invoice={inv} />}
                      </Fragment>
                    );
                  })}
                </tbody>
                <tfoot>
                  <tr className="border-t-2 border-gray-200 bg-gray-50">
                    <td colSpan={5} className="px-4 py-3 text-xs text-gray-500 font-medium hidden lg:table-cell">Totales</td>
                    <td className="px-4 py-3 text-xs text-gray-500 font-medium lg:hidden" colSpan={3}>Totales</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-800 hidden lg:table-cell">{numberFmt.format(totalGravadas)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-gray-600 hidden lg:table-cell">{numberFmt.format(totalIva)}</td>
                    <td className="px-4 py-3 text-right tabular-nums font-bold text-brand-700">{numberFmt.format(totalPagar)}</td>
                    <td />
                  </tr>
                </tfoot>
              </table>
            </div>
          </Panel>
        </>
      )}
    </div>
  );
}
